import { Descriptions, Flex, Tag, Typography } from 'antd'
import { Button } from 'antd'
import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { OrgChartNode } from './types'

const { Title, Text } = Typography

const TYPE_STYLES: Record<
  string,
  { bg: string; border: string; text: string }
> = {
  company: { bg: '#f0faf8', border: '#0F766E', text: '#0F766E' },
  division: { bg: '#e6f7ff', border: '#69b1ff', text: '#1677ff' },
  department: { bg: '#f9f0ff', border: '#b37feb', text: '#722ed1' },
  team: { bg: '#f6ffed', border: '#95de64', text: '#389e0d' }
}

type NodeDetailCardProps = {
  node: OrgChartNode
  breadcrumb: string
  onClose: () => void
}

export const NodeDetailCard = ({
  node,
  breadcrumb,
  onClose
}: NodeDetailCardProps) => {
  const { t } = useTranslation()
  const style = TYPE_STYLES[node.type] ?? TYPE_STYLES.company

  return (
    <div
      style={{
        border: `2px solid ${style.border}`,
        borderRadius: 8,
        overflow: 'hidden',
        marginTop: 24
      }}
    >
      <Flex
        justify="space-between"
        align="center"
        style={{
          background: style.bg,
          padding: '12px 16px',
          borderBottom: `1px solid ${style.border}`
        }}
      >
        <div>
          <Flex align="center" gap={8}>
            <Title level={5} style={{ margin: 0 }}>
              {node.name}
            </Title>
            <Tag
              style={{
                background: style.bg,
                color: style.text,
                border: `1px solid ${style.border}`
              }}
            >
              {t(`workspace.orgChart.types.${node.type}`)}
            </Tag>
          </Flex>
          {breadcrumb && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {breadcrumb}
            </Text>
          )}
        </div>
        <Button type="text" icon={<X size={18} />} onClick={onClose} />
      </Flex>
      <div style={{ padding: 16 }}>
        <Descriptions column={3} size="small">
          {node.owner && (
            <Descriptions.Item label={t('workspace.orgChart.nodeDetail.owner')}>
              <Text strong>{node.owner.name}</Text>
              {node.owner.title && node.owner.title !== node.owner.name
                ? ` — ${node.owner.title}`
                : null}
            </Descriptions.Item>
          )}
          <Descriptions.Item label={t('workspace.orgChart.nodeDetail.members')}>
            {node.memberCount}
          </Descriptions.Item>
          {node.children.length > 0 && (
            <Descriptions.Item
              label={t('workspace.orgChart.nodeDetail.childUnits')}
            >
              {node.childCount}{' '}
              {t(
                `workspace.orgChart.types.${node.children[0]?.type ?? 'team'}`
              )}
              (s)
            </Descriptions.Item>
          )}
        </Descriptions>
        {node.children.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t('workspace.orgChart.nodeDetail.directReports')}:
            </Text>
            <Flex gap={8} wrap="wrap" style={{ marginTop: 4 }}>
              {node.children.map((child) => {
                const childStyle = TYPE_STYLES[child.type] ?? TYPE_STYLES.team

                return (
                  <Tag
                    key={child.id}
                    style={{
                      background: childStyle.bg,
                      color: childStyle.text,
                      border: `1px solid ${childStyle.border}`
                    }}
                  >
                    {child.name} ({child.memberCount})
                  </Tag>
                )
              })}
            </Flex>
          </div>
        )}
      </div>
    </div>
  )
}
